<template>
  <section class="play-challenge-hunt container">
    <card classes="wide">
      <template v-slot:title>
        <i class="fas fa-crosshairs" /> Photo Hunt
      </template>
      <div class="form-photo-container flex row">
        <div class="flex column sidebar">
          <h3>Find these items</h3>
          <em>Click an item and then click on the photo where you find it.</em>
          <div class="list hotspots">
            <div :class="hotspotItemClass(hotspot)" 
                 v-for="hotspot in hotspots" 
                 v-bind:key="hotspot.id" 
                 @click="hotspotItem_clickHandler(hotspot)">
              <div class="list-item-cell title">
                {{hotspot.title}}
              </div>
              <div class="list-item-cell hotspot-block" 
                   :style="hotspotBlockStyle(hotspot)" />
            </div>
          </div>
          <form class="form" v-on:submit.prevent="submitChallengeAnswer_onsubmitHandler">
            <div class="flex column">
              <div class="form-group">
                <button type="submit" :disabled="!valid">Answer</button>
                <div v-if="sending">
                  Uploading and saving...
                </div>
              </div>
              <div class="note anonymous-note" v-if="isAnonymous">
                You are playing anonymously, your answers will not be saved. Create an account or login to save your results.
              </div>
            </div>
            <div v-if="error" class="form-error">
              {{ error }}
            </div>
          </form>
        </div>
        <div class="flex column center">
          <photo-container :src="challenge.photos[0].url"
                           :answers="answers"
                           @answerClick="photoContainer_answerClickHandler"
                           @photoClick="photoContainer_photoClickHandler" />
          <client-only>
            <challenge-points-possible :challenge="challenge" v-if="!isMobile" />
          </client-only>
        </div>
      </div>
    </card>
  </section>
</template>

<script>
import Card from '~/components/Card';
import VueLayersMap from '~/components/VueLayersMap.vue';
import GlobalMixin from '~/mixins/GlobalMixin';
import ChallengeByIdQuery from '~/apollo/queries/ChallengeById.gql';
import ChallengePointsPossible from '~/components/ChallengePointsPossible';
import PhotoContainer from '~/components/PhotoContainer';
import Avatar from '~/components/Avatar';
import ImageZoomer from 'vue-zoomer';
import {challengeToSolutionUrl} from '~/utils/pqUtil';
import './index.scss';

const colors = ["#FFFF00","#1CE6FF","#FF34FF","#FF4A46","#008941","#006FA6","#A30059","#FFDBE5","#7A4900","#0000A6","#63FFAC","#B79762","#004D43","#8FB0FF","#997D87","#5A0007","#809693","#FEFFE6","#1B4400","#4FC601","#3B5DFF","#4A3B53","#FF2F80","#61615A","#BA0900","#6B7900","#00C2A0","#FFAA92","#FF90C9","#B903AA","#D16100","#DDEFFF","#000035","#7B4F4B","#A1C299","#300018","#0AA6D8","#013349","#00846F","#372101","#FFB500","#C2FFED","#A079BF","#CC0744","#C0B9B2","#C2FF99","#001E09","#00489C","#6F0062","#0CBD66","#EEC3FF","#456D75","#B77B68","#7A87A1","#788D66","#885578","#FAD09F","#FF8A9A","#D157A0","#BEC459","#456648","#0086ED","#886F4C","#34362D","#B4A8BD","#00A6AA","#452C2C","#636375","#A3C8C9","#FF913F","#938A81","#575329","#00FECF","#B05B6F","#8CD0FF","#3B9700","#04F757","#C8A1A1","#1E6E00","#7900D7","#A77500","#6367A9","#A05837","#6B002C","#772600","#D790FF","#9B9700","#549E79","#FFF69F","#201625","#72418F","#BC23FF","#99ADC0","#3A2465","#922329","#5B4534","#FDE8DC","#404E55","#0089A3","#CB7E98","#A4E804","#324E72","#6A3A4C","#83AB58","#001C1E","#D1F7CE","#004B28","#C8D0F6","#A3A489","#806C66","#222800","#BF5650","#E83000","#66796D","#DA007C","#FF1A59","#8ADBB4","#1E0200","#5B4E51","#C895C5","#320033","#FF6832","#66E1D3","#CFCDAC","#D0AC94","#7ED379","#012C58","#7A7BFF","#D68E01","#353339","#78AFA1","#FEB2C6","#75797C","#837393","#943A4D","#B5F4FF","#D2DCD5","#9556BD","#6A714A","#001325","#02525F","#0AA3F7","#E98176","#DBD5DD","#5EBCD1","#3D4F44","#7E6405","#02684E","#962B75","#8D8546","#9695C5","#E773CE","#D86A78","#3E89BE","#CA834E","#518A87","#5B113C","#55813B","#E704C4","#00005F","#A97399","#4B8160","#59738A","#FF5DA7","#F7C9BF","#643127","#513A01","#6B94AA","#51A058","#A45B02","#1D1702","#E20027","#E7AB63","#4C6001","#9C6966","#64547B","#97979E","#006A66","#391406","#F4D749","#0045D2","#006C31","#DDB6D0","#7C6571","#9FB2A4","#00D891","#15A08A","#BC65E9","#FFFFFE","#C6DC99","#203B3C","#671190","#6B3A64","#F5E1FF","#FFA0F2","#CCAA35","#374527","#8BB400","#797868","#C6005A","#3B000A","#C86240","#29607C","#402334","#7D5A44","#CCB87C","#B88183","#AA5199","#B5D6C3","#A38469","#9F94F0","#A74571","#B894A6","#71BB8C","#00B433","#789EC9","#6D80BA","#953F00","#5EFF03","#E4FFFC","#1BE177","#BCB1E5","#76912F","#003109","#0060CD","#D20096","#895563","#29201D","#5B3213","#A76F42","#89412E","#1A3A2A","#494B5A","#A88C85","#F4ABAA","#A3F3AB","#00C6C8","#EA8B66","#958A9F","#BDC9D2","#9FA064","#BE4700","#658188","#83A485","#453C23","#47675D","#3A3F00","#061203","#DFFB71","#868E7E","#98D058","#6C8F7D","#D7BFC2","#3C3E6E","#D83D66","#2F5D9B","#6C5E46","#D25B88","#5B656C","#00B57F","#545C46","#866097","#365D25","#252F99","#00CCFF","#674E60","#FC009C","#92896B"]

export default {
  components: {
    Card,
    VueLayersMap,
    ImageZoomer,
    Avatar,
    ChallengePointsPossible,
    PhotoContainer
  },
  mixins: [
    GlobalMixin
  ],
  data() {
    return {
      error: undefined,
      sending: false,
      valid: false,
      answers: [],
      selHotspot: undefined
    };
  },
  async asyncData({ params, app, redirect }) {
    const { id } = params;

    if (isNaN(parseInt(id))) {
      redirect(`/play`);
      return {};
    }

    // See if the user has already answered this.
    const challengeAnswer = await app.$pqapi.getChallengeAnswerByChallenge({ challengeId: id, $auth: app.$auth });

    if (challengeAnswer && challengeAnswer.statusCode !== 404 && challengeAnswer.statusCode !== 403) {
      redirect(`/play/hunt/${id}/solution`);
      return {};
    }

    const result = await app.apolloProvider.defaultClient.query({
      query: ChallengeByIdQuery, 
      variables: { id }
    });

    return { challenge: result.data.challenge };
  },
  computed: {
    title() {
      return this.challenge ? this.challenge.challenge_type.name + ' Challenge' : 'N/A';
    },
    answerAsText() {
      if (this.valid) {
        return this.answer.datePhotoTaken + ' at ' + this.answer.location.placeName;
      }

      return '';
    },
    isAnonymous() {
      return !this.$auth.user;
    },
    isMobile() {
      return process.client ? window.innerWidth < 799 : false;
    },
    hotspots() {
      return this.challenge ? this.challenge.challenge_details.hotspots : [];
    }
  },
  methods: {
    hotspotAnswered(hotspot) {
      return this.answers.find(a => a.hotspot === hotspot);
    },
    validate() {
      this.valid = this.answers.length > 0;
    },
    hotspotItemClass(hotspot) {
      return hotspot === this.selHotspot ? 'list-item selected' : 'list-item';
    },
    hotspotBlockStyle(hotspot) {
      const hotspotHasAnswer = this.answers.find(a => a.hotspot === hotspot);

      if (!hotspotHasAnswer) {
        return {
          display: 'none'
        }
      }
      return {
        background: colors[hotspot.id]
      }
    },
    mapCoordinateChangeHandler(event) {
      const self = this;
      
      this.answer.location.longitude = event.coordinate[0];
      this.answer.location.latitude = event.coordinate[1];

      this.selectedLatLon = event.coordinate;

      this.$OpenStreetMapAPI.reverseLatLong({
        latitude: this.answer.location.latitude,
        longitude: this.answer.location.longitude
      }).then(json => {
        if (!json.error) {
          self.answer.location.details = json;
          self.answer.location.placeName = json.display_name;
        } else {
          self.answer.location.placeName = undefined;
        }
      });
    },
    submitChallengeAnswer_onsubmitHandler() {
      const self = this;
      this.$pqapi.postChallengeAnswer({
        $auth: this.$auth,
        challengeId: this.challenge.id,
        answers: this.answers
      }).then(result => {
        // Are we anonymous? Store the state before we transition to the solution page.
        if (!self.$auth.user) {
          self.$store.commit('anonymous/setLastChallenge', {
            challenge: this.challenge,
            answerResults: result,
            answers: self.answers
          });
        } else {
          this.$auth.setUser({
            ...this.$auth.user, 
            status: result.newUserStatus
          });
        }

        self.$router.push(challengeToSolutionUrl(self.challenge));
      });
    },
    photoContainer_photoClickHandler(answer) {
      if (this.selHotspot) {
        const indexOfExisting = this.answers.findIndex(a => a.hotspot === this.selHotspot);

        if (indexOfExisting !== -1) {
          // Splice existing answer
          this.answers.splice(indexOfExisting, 1);
        }

        this.answers = [...this.answers, { 
          ...answer,
          hotspot: this.selHotspot
        }];

        this.validate();
      }
    },
    hotspotItem_clickHandler(hotspot) {
      this.selHotspot = hotspot;
    },
    photoContainer_answerClickHandler(answer) {
      // Delete the answer
      const ix = this.answers.indexOf(answer);
      this.answers = this.answers.concat();
      this.answers.splice(ix, 1);
    }
  },
  watch: {
    "answer.datePhotoTaken": function() {
      this.validate();
    },
    "answer.location.longitude": function() {
      this.validate();
    }
  }
}
</script>
